import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../api";

// Helpers partagés (refonte 20/10) : formats, polling, badges, filtres de périmètre, toasts, modale.

export const f2 = (n) => Number(n || 0).toFixed(2);
export const f3 = (n) => (n == null || !Number.isFinite(Number(n)) ? "—" : Number(n).toFixed(3));
export const signed = (n, d = 2, suffix = "") => (n == null || !Number.isFinite(Number(n)) ? "—" : (Number(n) >= 0 ? "+" : "") + Number(n).toFixed(d) + suffix);
export const pct = (n, d = 1) => (n == null || !Number.isFinite(Number(n)) ? "—" : Number(n).toFixed(d) + " %");
export const money = (n, d = 2) => (n == null || !Number.isFinite(Number(n)) ? "—" : Number(n).toFixed(d) + " $");

export function minutesSince(d) {
  const t = new Date(d).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}
// « il y a 3 min », « il y a 2 h », « hier 21:40 », « 12/10 »
export function ago(d) {
  const m = minutesSince(d);
  if (m == null) return "—";
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  if (m < 36 * 60) return `il y a ${Math.round(m / 60)} h`;
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}
export function when(d) {
  if (!d) return "—";
  const x = new Date(d), now = new Date();
  const sameDay = x.toDateString() === now.toDateString();
  const hm = x.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return hm;
  return x.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + hm;
}

// Polling simple : recharge toutes les `ms`, conserve les dernières données en cas d'erreur.
export function usePoll(load, ms, deps = []) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let alive = true;
    const run = () => load().then((d) => { if (alive) { setData(d); setErr(""); } }).catch((e) => { if (alive) setErr(e.message || "Erreur"); });
    run();
    const t = ms > 0 ? setInterval(run, ms) : null;
    return () => { alive = false; if (t) clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps]);
  return { data, err, refresh };
}

// Réglages légers (mode, armement) — pollés toutes les 30 s.
export function useModeSettings() {
  const { data } = usePoll(() => api("/settings").then((d) => d?.settings || null), 30000);
  return data;
}

export function ErrorBanner({ err }) {
  if (!err) return null;
  return <div className="notice notice-down" style={{ marginBottom: 14 }}>{err} <span className="muted small">— dernières données conservées, nouvel essai au prochain rafraîchissement.</span></div>;
}

export function Loading() { return <div className="app-center"><div className="spinner" /></div>; }

export function Empty({ children }) { return <div className="table-empty">{children}</div>; }

// ——— Toasts (remplacent alert()) ———
const ToastCtx = createContext(() => {});
export function ToastProvider({ children }) {
  const [t, setT] = useState(null);
  const show = useCallback((text, kind = "") => {
    setT({ text, kind, id: Date.now() });
    setTimeout(() => setT((cur) => (cur && Date.now() - cur.id >= 3400 ? null : cur)), 3600);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {t && <div className={"toast " + t.kind}>{t.text}</div>}
    </ToastCtx.Provider>
  );
}
export function useToast() { return useContext(ToastCtx); }

// ——— Périmètre SIM / RÉEL ———
export const SCOPES = [["", "Mode courant"], ["live", "Réel"], ["sim", "Simulation"], ["all", "Tout"]];
export function ScopeSelect({ scope, onChange, current }) {
  return (
    <select className="select" style={{ width: "auto", padding: "5px 9px", fontSize: 12.5 }} value={scope} onChange={(e) => onChange(e.target.value)} title="Périmètre des paris affichés">
      {SCOPES.map(([k, l]) => <option key={k || "cur"} value={k}>{l}{k === "" && current ? ` (${current === "live" ? "réel" : "simulation"})` : ""}</option>)}
    </select>
  );
}
export function scopeQuery(scope) { return scope ? `&scope=${scope}` : ""; }

// ——— Voies ———
export const LANES = [
  { key: "", label: "Toutes" }, { key: "copy", label: "Copie" }, { key: "ia", label: "Analyse IA" }, { key: "book", label: "Cotes" },
  { key: "nearend", label: "Fin de marché" }, { key: "learn", label: "Apprentissage" }, { key: "paper", label: "Pilotes" },
];
export function laneLabel(lane) {
  const k = String(lane || "");
  if (!k) return "Analyse IA";
  if (k === "copy") return "Copie";
  if (k === "book") return "Cotes";
  if (k === "nearend") return "Fin de marché";
  if (k === "learn") return "Apprentissage";
  if (k.startsWith("paper:")) return "Pilote";
  return k;
}
export function LaneBadge({ lane }) {
  const k = String(lane || "");
  const cls = k === "copy" ? "badge-accent" : k.startsWith("paper:") ? "badge-warn" : "";
  return <span className={"badge " + cls} title={k.startsWith("paper:") ? k : undefined}>{laneLabel(k)}</span>;
}

// ——— Statuts ———
export const STATUS_LABELS = {
  placed: ["badge-accent", "En cours"], approving: ["badge-accent", "Placement…"], exiting: ["badge-warn", "Vente en cours"],
  settling: ["badge-warn", "Règlement…"], won: ["badge-up", "Gagné"], lost: ["badge-down", "Perdu"],
  rejected: ["", "Rejeté"], approved: ["badge-warn", "Signal validé"], pending_validation: ["badge-warn", "À valider"],
  proposed: ["", "Proposé"], void: ["", "Remboursé"], exited: ["badge-warn", "Vendu"],
};
export function StatusBadge({ status }) {
  const [cls, txt] = STATUS_LABELS[status] || ["", status];
  return <span className={"badge " + cls}>{txt}</span>;
}
export const OPEN_STATUSES = ["placed", "approving", "exiting", "settling"];
export const isOpenBet = (b) => OPEN_STATUSES.includes(b?.status);

export function SimBadge({ bet }) {
  // `simulated` absent (projection serveur) = inconnu : on n'affiche rien plutôt qu'un faux « SIM ».
  if (bet?.simulated === true) return <span className="badge" style={{ fontSize: 10 }} title="Simulation">SIM</span>;
  return null;
}
export function ExecBadges({ bet }) {
  if (!bet) return null;
  const out = [];
  if (bet.fillPending) out.push(<span key="fp" className="badge badge-warn" title="Ordre envoyé, exécution non confirmée">fill à confirmer{bet.fillPendingSince ? ` · ${minutesSince(bet.fillPendingSince)} min` : ""}</span>);
  if (bet.pendingSell) out.push(<span key="ps" className="badge badge-warn" title="Vente envoyée, non confirmée">vente en attente{bet.pendingSellSince ? ` · ${minutesSince(bet.pendingSellSince)} min` : ""}</span>);
  if (bet.redeemPending && (bet.payoutDue || 0) > 0) out.push(<span key="rd" className="badge badge-up" title="Gain de résolution à réclamer on-chain">à réclamer · {f2(bet.payoutDue)} $</span>);
  if (bet.inPlayArtifact) out.push(<span key="ar" className="badge" title={bet.inPlayArtifactReason || "Rempli à prix périmé (in-play) — exclu de l'apprentissage"}>artefact</span>);
  return out.length ? <span className="chips" style={{ gap: 4 }}>{out}</span> : null;
}

export function polyLink(bet) {
  if (bet?.marketSlug) return `https://polymarket.com/market/${bet.marketSlug}`;
  return "https://polymarket.com/portfolio";
}

export function Modal({ title, children, onCancel, onConfirm, confirmLabel = "Confirmer", busy, danger }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={busy ? undefined : onCancel}>
      <div className="card card-pad" style={{ maxWidth: 460, width: "100%", boxShadow: "var(--sh)" }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: 10 }}>{title}</h2>
        <div style={{ fontSize: 13.5, marginBottom: 16 }}>{children}</div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onCancel} disabled={busy}>Annuler</button>
          {onConfirm && <button type="button" className={"btn btn-sm " + (danger ? "btn-danger" : "btn-primary")} onClick={onConfirm} disabled={busy}>{busy ? "…" : confirmLabel}</button>}
        </div>
      </div>
    </div>
  );
}

export function Stat({ label, value, unit, sub, cls }) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className={"value " + (cls || "")}>{value}{unit && <span className="unit">{unit}</span>}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export function Section({ title, hint, action, children, className = "" }) {
  return (
    <div className={"section " + className}>
      {(title || action) && (
        <div className="section-head">
          <div><h2>{title}</h2>{hint && <div className="hint">{hint}</div>}</div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Gauge({ label, value, max, suffix = " %", hint }) {
  const v = Number(value) || 0, m = Number(max) || 0;
  const ratio = m > 0 ? Math.min(1, v / m) : 0;
  const color = ratio >= 0.85 ? "var(--down)" : ratio >= 0.6 ? "var(--warn)" : "var(--up)";
  const d = suffix.trim() === "%" ? 1 : 0;
  return (
    <div className="gauge">
      <div className="head"><span>{label}</span><span className="mono" style={{ color }}>{v.toFixed(d)}{suffix}<span className="muted"> / {m.toFixed(d)}{suffix}</span></span></div>
      <div className="bar"><i style={{ width: (ratio * 100).toFixed(0) + "%", background: color }} /></div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}
